import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Language,
  Translate,
  Flag,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const LanguageSwitcher = () => {
  const { language, changeLanguage, translate } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isChanging, setIsChanging] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (newLanguage) => {
    if (newLanguage !== language) {
      setIsChanging(true);
      try {
        changeLanguage(newLanguage);
        handleClose();
      } catch (error) {
        console.error('Error changing language:', error);
      } finally {
        setIsChanging(false);
      }
    } else {
      handleClose();
    }
  };

  const getLanguageFlag = (lang) => {
    return lang === 'es' ? '🇪🇸' : '🇺🇸';
  };

  const getLanguageName = (lang) => {
    return lang === 'es' ? 'Español' : 'English';
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={isChanging}
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isChanging ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
              {getLanguageFlag(language)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {language.toUpperCase()}
          </Typography>
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            minWidth: 200,
          },
        }}
      >
        <MenuItem 
          onClick={() => handleLanguageChange('en')}
          selected={language === 'en'}
          disabled={isChanging}
          sx={{
            '&.Mui-selected': {
              background: 'rgba(102, 126, 234, 0.1)',
            },
          }}
        >
          <ListItemIcon>
            <Typography variant="h6">🇺🇸</Typography>
          </ListItemIcon>
          <ListItemText 
            primary="English"
            secondary="English"
          />
          {language === 'en' && (
            <Chip 
              label="Current" 
              size="small" 
              color="primary" 
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </MenuItem>

        <MenuItem 
          onClick={() => handleLanguageChange('es')}
          selected={language === 'es'}
          disabled={isChanging}
          sx={{
            '&.Mui-selected': {
              background: 'rgba(102, 126, 234, 0.1)',
            },
          }}
        >
          <ListItemIcon>
            <Typography variant="h6">🇪🇸</Typography>
          </ListItemIcon>
          <ListItemText 
            primary="Español"
            secondary="Spanish"
          />
          {language === 'es' && (
            <Chip 
              label="Actual" 
              size="small" 
              color="primary" 
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

export default LanguageSwitcher; 